import { useQuery } from "@tanstack/react-query";
import { groupLabel, LEVEL_OPTIONS, notify, NOTIFY_LEVEL, prefixOf, TYPE_GROUPS, type NotifyLevel, type NotifyType } from "@/app/notify";
import { useSave } from "@/app/useSave";
import { Alert, Button, EmptyState, Pill, SectionTitle, Select, Switch, Table, TableWrap, Th } from "@/ui";

/** 按前缀分组，组的顺序按 TYPE_GROUPS，表外的前缀排在最后；组内保持接口给的 type 顺序。 */
function groupTypes(rows: NotifyType[]): [string, NotifyType[]][] {
  const known = TYPE_GROUPS.map(([p]) => p);
  const prefixes = [...new Set(rows.map((r) => prefixOf(r.type)))].sort((a, b) => rank(known, a) - rank(known, b));
  return prefixes.map((p) => [p, rows.filter((r) => prefixOf(r.type) === p)]);
}
const rank = (known: string[], p: string) => (known.includes(p) ? known.indexOf(p) : known.length);

/** “事件类型”页签：按 type 前缀分组，逐行开关与改等级，改过的行标出并可恢复默认；locked 的 system.test 只读（docs/04 第 15 节，D210）。 */
export function NotifyTypes() {
  const q = useQuery({ queryKey: ["notify-types"], queryFn: notify.types });
  const groups = groupTypes(q.data ?? []);
  return (
    <div className="ax-stack">
      {q.error && <Alert tone="danger" title="加载失败">{q.error.message}</Alert>}
      {q.isPending && <EmptyState title="加载中" />}
      {q.data && !groups.length && <EmptyState title="没有事件类型" />}
      {groups.map(([prefix, rows]) => (
        <section key={prefix} className="ax-section">
          <SectionTitle>{groupLabel(prefix)}（{prefix}）</SectionTitle>
          <TableWrap>
            <Table dense>
              <thead><tr><Th w="wide">事件</Th><Th w={220}>类型</Th><Th w={120}>启用</Th><Th w={140}>等级</Th><Th w={100}>状态</Th><Th /></tr></thead>
              <tbody>{rows.map((t) => <TypeRow key={t.type} t={t} />)}</tbody>
            </Table>
          </TableWrap>
        </section>
      ))}
    </div>
  );
}

/** 一行类型：开关与等级改完立即保存（PUT），等级选回默认值时传 null 以跟随代码默认；恢复默认是 DELETE。 */
function TypeRow({ t }: { t: NotifyType }) {
  const put = useSave((b: { enabled: boolean; level: NotifyLevel | null }) => notify.saveType(t.type, b), { invalidate: ["notify-types"], message: `已更新 ${t.name}` });
  const reset = useSave(() => notify.resetType(t.type), { invalidate: ["notify-types"], message: `已恢复 ${t.name} 的默认设置` });
  const busy = put.saving || reset.saving;
  const error = put.error ?? reset.error;
  const setLevel = (l: string) => put.save({ enabled: t.enabled, level: l === t.default_level ? null : (l as NotifyLevel) });
  return (
    <tr>
      <td>{t.name}</td>
      <td className="ax-mono">{t.type}</td>
      <td><Switch checked={t.locked || t.enabled} disabled={t.locked || busy} label={<span className="ax-sr-only">启用 {t.name}</span>}
        onChange={(enabled) => put.save({ enabled, level: t.override?.level ?? null })} /></td>
      <td><Select size="sm" ariaLabel={`${t.name}的等级`} options={LEVEL_OPTIONS} value={t.level} disabled={t.locked || busy} onChange={setLevel} /></td>
      <td>{t.locked ? <Pill plain>固定发送</Pill> : t.override ? <Pill tone="brand" title={`默认：${t.default_enabled ? "启用" : "停用"}，${NOTIFY_LEVEL[t.default_level].label}`}>已修改</Pill> : <span className="ax-secondary">默认</span>}</td>
      <td>
        {t.override && !t.locked && <Button size="sm" variant="ghost" disabled={busy} aria-label={`恢复${t.name}的默认设置`} onClick={() => reset.save(undefined)}>恢复默认</Button>}
        {error && <span className="ax-field-error" role="alert">{error}</span>}
      </td>
    </tr>
  );
}
